// Program Signal Analysis — deep-dive comparison of NGO/partner-reported
// figures vs Voice AI signal collected from beneficiaries. Surfaces divergence
// (blind spots), convergence (validated outcomes) and early-warning gaps.
//
// Per indicator:
//   ngo   = partner-reported rate (%)
//   voice = Voice AI signal (%)
//   tone  = "critical" | "warning" | "convergence"  (drives bar/chip colour)
//   note  = short label for a special chip (e.g. "blind spot")

export const DEEP_DIVES = [
  // ── Employability & Skilling ──────────────────────────────────────────────
  {
    id: "dd-emp",
    pillar: "employability",
    program: "Skills for Young Women — IT/ITES",
    partner: "TechnoServe India",
    location: "Maharashtra · Mumbai",
    beneficiaries: 1900,
    round: "Round 3 · Oct 2024",
    convergence: { value: 41, status: "Low — investigate", tone: "critical" },
    ngoHeadline: { label: "Placement rate (reported)", value: 78, sub: "Partner MIS · month 9" },
    voiceHeadline: { label: "Still employed (Voice AI)", value: 52, gap: -26, gapUnit: "pp gap" },
    calls: { value: 412, completionPct: 68 },
    indicators: [
      { label: "Placement rate",      ngo: 78, voice: 52, tone: "critical",    note: "−26 pp" },
      { label: "Job retention (3 mo)", ngo: 71, voice: 44, tone: "critical",    note: "−27 pp" },
      { label: "Salary as promised",  ngo: 90, voice: 66, tone: "warning",     note: "−24 pp" },
      { label: "Training satisfaction", ngo: 88, voice: 91, tone: "convergence", note: "aligned" },
    ],
    insights: [
      {
        severity: "critical",
        title: "Critical divergence — retention blind spot",
        body: "Partner reports 71% 3-month retention, but only 44% of beneficiaries confirm they are still employed. Voice AI surfaced that many roles were short-term BPO contracts that lapsed after placement was counted.",
        link: "Dig deeper into retention →",
      },
      {
        severity: "warning",
        title: "Salary inflation in reported figures",
        body: "A 24 pp gap on 'salary as promised' suggests offer letters were logged at headline CTC while take-home pay was materially lower. Recommend auditing payslips for the Oct cohort.",
        link: "Review salary evidence →",
      },
      {
        severity: "convergence",
        title: "Convergence confirmed — training quality",
        body: "Training satisfaction aligns closely (88% vs 91%). The curriculum and trainers are working well; the gap is downstream in placement quality, not learning.",
        link: "Explore training feedback →",
      },
    ],
  },

  // ── Sustainability ────────────────────────────────────────────────────────
  {
    id: "dd-sus",
    pillar: "sustainability",
    program: "E-Waste Circularity Drive",
    partner: "Saahas Zero Waste",
    location: "Karnataka · Bengaluru",
    beneficiaries: 820,
    round: "Round 2 · Sep 2024",
    convergence: { value: 38, status: "Low — investigate", tone: "critical" },
    ngoHeadline: { label: "Collection bins active", value: 92, sub: "Partner MIS · month 6" },
    voiceHeadline: { label: "Actually using bins (Voice AI)", value: 47, gap: -45, gapUnit: "pp gap" },
    calls: { value: 196, completionPct: 61 },
    indicators: [
      { label: "Bins installed & active", ngo: 92, voice: 88, tone: "convergence", note: "aligned" },
      { label: "Households using bins",   ngo: 84, voice: 47, tone: "critical",    note: "blind spot" },
      { label: "Aware of e-waste rules",  ngo: 70, voice: 41, tone: "critical",    note: "−29 pp" },
      { label: "Programme satisfaction",  ngo: 80, voice: 74, tone: "warning",     note: "−6 pp" },
    ],
    insights: [
      {
        severity: "critical",
        title: "Adoption blind spot — bins ≠ behaviour",
        body: "Infrastructure is in place (88% bins active confirmed) but only 47% of households actually use them. Most still discard chargers and cables in regular waste — an awareness and last-mile collection failure.",
        link: "Dig deeper into adoption →",
      },
      {
        severity: "warning",
        title: "Awareness lag",
        body: "Only 41% of beneficiaries understand what counts as e-waste vs the 70% the partner expected. Door-step demos and vernacular signage are needed before the next collection cycle.",
        link: "Plan awareness drive →",
      },
      {
        severity: "convergence",
        title: "Convergence confirmed — infrastructure",
        body: "Bin installation and uptime numbers match the ground reality. The capex side of the programme is delivering; the gap is purely on behaviour change.",
        link: "Explore bin uptime →",
      },
    ],
  },

  // ── STEM Education ────────────────────────────────────────────────────────
  {
    id: "dd-stem",
    pillar: "stem",
    program: "Lab-on-a-Tab Mobile Science",
    partner: "Agastya International Foundation",
    location: "Karnataka · Mysuru",
    beneficiaries: 4800,
    round: "Round 4 · Jul 2024",
    convergence: { value: 74, status: "High — aligned", tone: "convergence" },
    ngoHeadline: { label: "Sessions delivered (reported)", value: 95, sub: "Partner MIS · month 10" },
    voiceHeadline: { label: "Attended regularly (Voice AI)", value: 81, gap: -14, gapUnit: "pp gap" },
    calls: { value: 524, completionPct: 79 },
    indicators: [
      { label: "Session attendance",     ngo: 95, voice: 81, tone: "warning",     note: "−14 pp" },
      { label: "Concept comprehension",  ngo: 76, voice: 78, tone: "convergence", note: "aligned" },
      { label: "Interest in science",    ngo: 82, voice: 86, tone: "convergence", note: "aligned" },
      { label: "Teacher confidence",     ngo: 68, voice: 71, tone: "convergence", note: "aligned" },
    ],
    insights: [
      {
        severity: "convergence",
        title: "Strong convergence — learning outcomes",
        body: "Comprehension, science interest and teacher confidence all align within a few points of partner reports. The experiential model is genuinely shifting learning, not just attendance.",
        link: "Explore learning gains →",
      },
      {
        severity: "warning",
        title: "Attendance slightly overstated",
        body: "A 14 pp gap on attendance points to register inflation on days the mobile lab visited remote clusters. Minor, but worth GPS-tagging future sessions.",
        link: "Review attendance logs →",
      },
      {
        severity: "convergence",
        title: "Teacher capability building",
        body: "Teachers self-report rising confidence to run experiments independently — corroborated by Voice AI calls. A good signal for sustainability after the lab moves on.",
        link: "Dig deeper into teachers →",
      },
    ],
  },

  // ── Digital Literacy ──────────────────────────────────────────────────────
  {
    id: "dd-dgl",
    pillar: "digital",
    program: "Innovators of Tomorrow",
    partner: "Muskaan Dreams (with Motorola)",
    location: "Uttar Pradesh · Lucknow",
    beneficiaries: 3600,
    round: "Round 1 · Aug 2024",
    convergence: { value: 56, status: "Moderate — monitor", tone: "warning" },
    ngoHeadline: { label: "Module completion (reported)", value: 83, sub: "Partner MIS · month 5" },
    voiceHeadline: { label: "Can apply skills (Voice AI)", value: 58, gap: -25, gapUnit: "pp gap" },
    calls: { value: 308, completionPct: 64 },
    indicators: [
      { label: "Module completion",     ngo: 83, voice: 72, tone: "warning",     note: "−11 pp" },
      { label: "Skill application",     ngo: 79, voice: 58, tone: "critical",    note: "−21 pp" },
      { label: "Device/lab access",     ngo: 88, voice: 49, tone: "critical",    note: "blind spot" },
      { label: "Programme satisfaction", ngo: 85, voice: 87, tone: "convergence", note: "aligned" },
    ],
    insights: [
      {
        severity: "critical",
        title: "Infrastructure blind spot — lab access",
        body: "Partner reports 88% device access, but only 49% of students confirm regular hands-on time. Voice AI flagged one weekly lab period and frequent connectivity drops — students forget skills between sessions.",
        link: "Dig deeper into access →",
      },
      {
        severity: "warning",
        title: "Completion ≠ capability",
        body: "Modules are marked complete (83%) yet only 58% can apply what they learned. Recommend project-based assessment instead of completion-only tracking.",
        link: "Review assessment model →",
      },
      {
        severity: "convergence",
        title: "Convergence confirmed — motivation",
        body: "Satisfaction is high and aligned (85% vs 87%). Students love the programme; the constraint is infrastructure, not engagement.",
        link: "Explore student voice →",
      },
    ],
  },
];

export const CONVERGENCE_TONE = {
  critical:    { label: "Low",      text: "text-[#E2231A]", chip: "bg-red-50 text-[#E2231A] border-red-200",      bar: "#E2231A" },
  warning:     { label: "Moderate", text: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200",  bar: "#F59E0B" },
  convergence: { label: "High",     text: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "#16A34A" },
};

// Bar / chip colours for the indicator comparison & signal chart
export const INDICATOR_TONE = {
  critical:    { voice: "#E2231A", chip: "bg-red-50 text-[#E2231A] border border-red-200" },
  warning:     { voice: "#F59E0B", chip: "bg-amber-50 text-amber-700 border border-amber-200" },
  convergence: { voice: "#16A34A", chip: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

export const NGO_BAR_COLOR = "#9CA3AF";   // neutral grey for partner-reported
export const NGO_BAR_DARK   = "#4B5563";

export const INSIGHT_BORDER = {
  critical:    "border-l-4 border-l-[#E2231A]",
  warning:     "border-l-4 border-l-[#F59E0B]",
  convergence: "border-l-4 border-l-[#16A34A]",
};
